import React from "react";
import { Link } from "react-router-dom";
import { Star, Trophy, Award, Sparkles, Layers, Send } from "lucide-react";

/**
 * ShopCategoryLauncher — replaces the old grouped sticky slider nav.
 *
 * Six large tap-friendly buttons at the top of /shop. Uses a responsive
 * grid so:
 *   • Mobile (default):   2 columns × 3 rows — no horizontal scroll.
 *   • Tablet (sm):        3 columns × 2 rows.
 *   • Desktop (lg):       6 columns × 1 row — clean single band.
 *
 * Anchor buttons scroll to on-page sections. The Gold Singles button is
 * a Link because it navigates to a different route (/shop/gold-singles).
 *
 * Visual language matches CollectorUp — bold offset shadow, thick
 * black border, brand ink/yellow/red/white color chips.
 */
const OPTIONS = [
  { key: "featured",         type: "anchor", id: "featured",         label: "FEATURED\nITEMS",       Icon: Star,     tone: "white"  },
  { key: "gold-sets",        type: "anchor", id: "gold-team-sets",   label: "GOLD FULL\nSETS",       Icon: Trophy,   tone: "gold"   },
  { key: "blue-sets",        type: "anchor", id: "blue-team-sets",   label: "BLUE FULL\nSETS",       Icon: Award,    tone: "blue"   },
  { key: "white-sets",       type: "anchor", id: "white-team-sets",  label: "WHITE FULL\nSETS",      Icon: Layers,   tone: "ivory"  },
  { key: "gold-singles",     type: "route",  to: "/shop/gold-singles", label: "GOLD CRUMPLE\nSINGLES", Icon: Sparkles, tone: "yellow" },
  { key: "missing-stickers", type: "anchor", id: "missing-stickers", label: "MISSING\nSTICKERS",     Icon: Send,     tone: "red"    },
];

const TONES = {
  white:  "bg-white text-[var(--ink)] border-[var(--ink)] hover:bg-[var(--ink)] hover:text-white",
  gold:   "bg-[#d4af37] text-[var(--ink)] border-[var(--ink)] hover:brightness-110",
  blue:   "bg-[#1e4fb8] text-white border-[var(--ink)] hover:brightness-110",
  ivory:  "bg-neutral-50 text-[var(--ink)] border-[var(--ink)] hover:bg-white",
  yellow: "bg-[var(--panini-yellow)] text-[var(--ink)] border-[var(--ink)] hover:brightness-110",
  red:    "bg-[var(--panini-red)] text-white border-[var(--ink)] hover:brightness-110",
};

function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  window.history.replaceState(null, "", `#${id}`);
  el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function LauncherContent({ Icon, label }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-4 px-2 min-h-[92px] md:min-h-[104px]">
      <Icon size={22} strokeWidth={2.2} className="shrink-0" />
      <span className="text-[11px] sm:text-xs md:text-sm font-display font-bold uppercase tracking-wide text-center leading-[1.1] whitespace-pre-line">
        {label}
      </span>
    </div>
  );
}

export default function ShopCategoryLauncher() {
  return (
    <section
      id="shop-category-launcher"
      className="container-gm pt-6 md:pt-10 pb-2 md:pb-4"
      data-testid="shop-category-launcher"
    >
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        {OPTIONS.map(({ key, type, id, to, label, Icon, tone }) => {
          const base = `rounded-2xl border-2 transition shadow-[4px_4px_0_var(--ink)] hover:shadow-[6px_6px_0_var(--ink)] hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus:ring-2 focus:ring-[var(--panini-yellow)] ${TONES[tone]}`;
          if (type === "route") {
            return (
              <Link
                key={key}
                to={to}
                className={base}
                data-testid={`shop-launcher-${key}`}
              >
                <LauncherContent Icon={Icon} label={label} />
              </Link>
            );
          }
          return (
            <button
              key={key}
              type="button"
              onClick={() => scrollToId(id)}
              className={base + " w-full"}
              data-testid={`shop-launcher-${key}`}
            >
              <LauncherContent Icon={Icon} label={label} />
            </button>
          );
        })}
      </div>
    </section>
  );
}
